import { TRANSFER_EXECUTE_V2_PREFIX, TRANSFER_EXECUTE_V3_PREFIX } from "util/v3UxConfig";

export const TRANSFER_GROUPS = {
    Internal: "internal",
    ThirdParties: "thirdParties",
    ToContact: "toContact",
};

export const TRANSFER_INTERNAL_FORM_ID = "transferInternal";

export const TRANSFER_TO_CONTACT_FORM_ID = "transferToContact";

export const TRANSFER_THIRD_PARTIES_FORM_ID = "transferThirdParties";

/** @type {"/v2/transfer/transfer"} */
export const TRANSFER_EXECUTE_LEGACY_ENTRY_PATH = `${TRANSFER_EXECUTE_V2_PREFIX}/transfer`;

export const CBU_MAX_LENGTH = 22;

/**
 * Matches v2 and v3 transfer execute paths for a given type and optional suffix.
 *
 * @param {string|undefined|null} pathname
 * @param {"internal"|"thirdParties"|"toContact"} type
 * @param {"modify"|"back"} [suffix]
 * @returns {boolean}
 */
export function matchTransferExecutePath(pathname, type, suffix) {
    if (!pathname || typeof pathname !== "string" || !type) {
        return false;
    }
    const normalized = pathname.replace(/\/$/, "") || "/";
    const suffixPart = suffix ? `/${suffix}` : "";
    const v2Path = `${TRANSFER_EXECUTE_V2_PREFIX}/${type}${suffixPart}`;
    const v3Path = `${TRANSFER_EXECUTE_V3_PREFIX}/${type}${suffixPart}`;
    return normalized === v2Path || normalized === v3Path;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isTransferExecuteEntryPath(pathname) {
    return pathname === TRANSFER_EXECUTE_LEGACY_ENTRY_PATH;
}

/**
 * @param {string|number} value
 * @returns {number}
 */
export function parseTransferAmount(value) {
    if (typeof value === "string") {
        return parseFloat(value.replaceAll(".", "").replace(",", "."));
    }
    return value;
}

/**
 * SelectFieldV3 stores `{ value, label }`; legacy paths may use plain strings.
 *
 * @param {unknown} field
 * @returns {string}
 */
export function resolveTransferSelectValue(field) {
    if (field === null || field === undefined || field === "") {
        return "";
    }
    if (typeof field === "object" && "value" in field) {
        const optionValue = field.value;
        if (optionValue === null || optionValue === undefined || optionValue === "") {
            return "";
        }
        return String(optionValue);
    }
    return String(field);
}

/**
 * Normalizes amount field values (string, number, or `{ amount }` from legacy state).
 *
 * @param {unknown} value
 * @returns {number}
 */
export function resolveTransferAmountValue(value) {
    if (value === undefined || value === null || value === "") {
        return 0;
    }
    if (typeof value === "object" && value !== null && "amount" in value) {
        return resolveTransferAmountValue(value.amount);
    }
    const parsed = parseTransferAmount(value);
    return typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : 0;
}

/**
 * Applies TAD hint side-effects. Auto-opens the warning modal only until the user dismisses it
 * once for the current TAD amount session (V2 parity: modal unmounts on submit loading).
 *
 * @param {boolean} isTadTransaction
 * @param {{
 *   setPopupVisible: (visible: boolean) => void,
 *   setShowed: (showed: boolean) => void,
 *   isAcknowledged: () => boolean,
 *   resetAcknowledged: () => void,
 *   onActive?: () => void,
 *   onInactive?: () => void,
 * }} handlers
 */
export function applyTransferTadHint(isTadTransaction, handlers) {
    const { setPopupVisible, setShowed, isAcknowledged, resetAcknowledged, onActive, onInactive } = handlers;

    if (isTadTransaction) {
        if (onActive) {
            onActive();
        }
        setShowed(true);
        if (!isAcknowledged()) {
            setPopupVisible(true);
        }
        return;
    }

    resetAcknowledged();
    setPopupVisible(false);
    setShowed(false);
    if (onInactive) {
        onInactive();
    }
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidAlias(value) {
    const reg = /^(?:(?![ñÑ])[a-zA-Z.\-\d])+$/;
    return reg.test(value);
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidCbu(value) {
    const reg = /^\d+$/;
    return reg.test(value) && value.length === CBU_MAX_LENGTH;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidCbuAlias(value) {
    return isValidCbu(value) || isValidAlias(value);
}

/**
 * Normalizes account references from transaction/draft data ({ value }, { idProduct }, or plain id).
 *
 * @param {string|{ value?: string, idProduct?: string }|undefined|null} accountRef
 * @returns {string|undefined}
 */
export function resolveTransferAccountRef(accountRef) {
    if (accountRef == null || accountRef === "") {
        return undefined;
    }
    if (typeof accountRef === "object") {
        return accountRef.value || accountRef.idProduct || undefined;
    }
    return accountRef;
}
